import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await forgotPassword(email);
      setSent(true);
      toast.success('If an account exists with this email, a reset link has been sent.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-8">
        <Card className="w-full max-w-md shadow-md">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src="/ors-logo.svg" 
                alt="Onsite Rail Solutions" 
                className="h-12 w-auto"
                srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
              />
            </div>

            <div className="w-16 h-16 bg-[#005EF8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#005EF8]" />
            </div>

            <h1 className="text-2xl font-bold text-[#323033] mb-2">Check your email</h1>
            <p className="text-[#5A5A5A]">
              We've sent a password reset link to <strong>{email}</strong>. 
              The link will expire in 1 hour.
            </p>
          </CardHeader>
          
          <CardContent className="pt-4">
            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </Button>
            </Link>

            <p className="mt-6 text-sm text-[#5A5A5A] text-center">
              Didn't receive the email?{' '}
              <button
                onClick={() => setSent(false)}
                className="text-[#005EF8] hover:text-[#0047C2] font-medium"
              >
                Try again
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-8">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-4 pb-2">
          <div className="flex justify-center">
            <img 
              src="/ors-logo.svg" 
              alt="Onsite Rail Solutions" 
              className="h-12 w-auto"
              srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
              data-testid="forgot-password-logo"
            />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#323033] mb-2">Forgot your password?</h1>
            <p className="text-sm text-[#5A5A5A]">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-11"
              disabled={loading}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 mt-6 text-sm text-[#5A5A5A] hover:text-[#323033]"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
